/**
 * THE GLYPH BESIDE A TINT (P38, task 1 — reference §8)
 *
 * =========================================================================
 * WHY THIS FILE EXISTS AT ALL
 * =========================================================================
 * §8: *"colour never works alone: every pill carries a glyph and the word, so it
 * survives colour-blindness and a greyscale print."* The word comes from
 * `lib/labels.ts`, which **imports nothing** on purpose — one module, no graph,
 * so it cannot pull a React component into a place that only holds vocabulary.
 * A lucide icon is a React component. So `labels.ts` names the glyph
 * (`TICKET_STATUS_GLYPH`, a plain string) and this file is the one place that
 * says which drawing that name is.
 *
 * Two files, one decision each: `labels.ts` decides WHICH glyph a status has,
 * this file decides WHAT that glyph looks like. Neither can invent a word and
 * neither can invent a tint.
 *
 * =========================================================================
 * WHY A NEUTRAL PILL CARRIES NO GLYPH
 * =========================================================================
 * The rule's purpose is that *colour* must not be the only carrier. A neutral
 * pill has no colour doing any work — it is grey precisely because the tint is
 * saying nothing — so there is nothing for a glyph to duplicate, and the word is
 * already the whole statement.
 *
 * That is not a loophole, it is the geometry. P37 sized the Status column for
 * *Draft waiting for approval* (157 px of a 176 px column at 1440, ellipsised on
 * 9 of 9 rows). A glyph plus its gap costs ~16 px. The three longest status
 * words — *Draft waiting for approval*, *Waiting on the supplier*, *Waiting on
 * Sales* — are all NEUTRAL under §8's table, so drawing a glyph only where the
 * tint means something spends the width exactly where there is room for it and
 * spends none where there is not. Measured after: overflow unchanged.
 *
 * `h-3 w-3` = 12 px, inside a 12 px pill: the glyph is the height of the text
 * beside it, not a decoration hanging off it.
 */
import { Check, CircleDashed, Clock, HelpCircle, MailWarning, PauseCircle, X } from 'lucide-react';
import type { GlyphName } from '../../lib/labels';

const GLYPHS: Record<GlyphName, typeof Check> = {
    /** `New` — the agent has it and nobody is waiting on a person. */
    dot: CircleDashed,
    /** `NeedsReview` — a question somebody has to answer. */
    question: HelpCircle,
    /** Waiting on another desk: Sales, or an approver. */
    clock: Clock,
    /** Waiting on somebody outside the house. */
    pause: PauseCircle,
    /** Finished. */
    check: Check,
    /** Dropped without an answer — a decision, so grey and never red (§8). */
    x: X,
    /**
     * Approved and still in the building (P37a). Deliberately not a `check`: the
     * whole point of that word is that the approval happened and the mail did
     * not go, and a tick is what a reader would take for "done".
     */
    held: MailWarning,
};

export function Glyph({ name, className = 'h-3 w-3 shrink-0' }: { name: GlyphName; className?: string }) {
    const Icon = GLYPHS[name];
    if (!Icon) return null;
    return <Icon aria-hidden="true" className={className} />;
}

/**
 * The glyph for a tone, for a pill whose word is not a ticket status — a review
 * reason, a draft warning, a row marker. `neutral` returns nothing, for the
 * reason in the header.
 */
export function toneGlyph(tone: 'neutral' | 'info' | 'warn' | 'done' | 'err'): GlyphName | null {
    switch (tone) {
        case 'warn': return 'question';
        case 'info': return 'dot';
        case 'done': return 'check';
        case 'err': return 'x';
        default: return null;
    }
}
